import styled from 'styled-components';
import { Dialog, Icon } from '@material-ui/core';
import { ContainedTextField } from '../../../Form/FinalForm/Contained/ConainedTextField';
import { required, validateMinBid, validateReservePrice } from '../../../../utils/validators';
import {
  useGetVehicleByIdQuery,
  useGetVinInfoByVehicleIdQuery,
  useGetVinInfoQueryState,
  useUpdateVehicleFieldMutation
} from '../../../../services/api/upload-vehicle';
import { Form, useField, useForm, useFormState } from 'react-final-form';
import { ContainedFormReactSelect } from '../../../Form/FinalForm/Contained/ContainedFormReactSelect';
import { ContainedNumberField } from '../../../Form/FinalForm/Contained/ContainedNumberField';
import { normalizeFormattedField } from '../../../../utils';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { BlueBookInfo } from '../../../../services/api/upload-vehicle/types';
import { OptionalEquipment } from './OptionalEquipment';
import arrayMutators from 'final-form-arrays';
import { KbbIcon } from '../../../../pages/CreateVehicle/icons/KbbIcon';
import {
  closeCarInfoDialog,
  closeVinDialog,
  openInitialPictureDialog,
  useCarInfoDialogOpened
} from '../../../../services/listerUploadVehicle/v2';
import { Vehicle } from 'types/vehicle';
import { MileageFields } from './MileageFields';
import { CancelAction, SubmitAction } from './ui';
import { useDeviceInfo } from 'hooks/useDeviceInfo';
import { serializeUploadInitialValues } from 'utils/uploadVehicle';

const StyledDialog = styled(Dialog)`
  .create-vehicle-info-paper {
    margin: auto 13px;
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
    border-radius: 20px;
    width: 100%;
  }
`;

const Wrapper = styled.form`
  display: flex;
  gap: 24px;
  flex-direction: column;
  padding: 26px 24px 24px;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  font-size: 22px;
  font-weight: 900;
  line-height: 25.78px;
  color: #000;
`;

const CloseIcon = styled(Icon)`
  width: 24px;
  height: 24px;
`;

const InputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 12px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: 19px;
`;

const KbbIconWrapper = styled.div`
  position: absolute;
  right: 20px;
  top: 72px;
  width: 69px;
  height: 39px;
  & svg {
    width: 100%;
    height: 100%;
  }
`;

export const VinInfoDialog = () => {
  const { vehicleId, type } = useCarInfoDialogOpened();

  const [updateVehicle] = useUpdateVehicleFieldMutation();

  const { data: vehicle, refetch } = useGetVehicleByIdQuery(vehicleId!, { skip: !vehicleId });

  const history = useHistory();
  const dispatch = useDispatch();

  const close = () => {
    dispatch(closeCarInfoDialog());
    dispatch(closeVinDialog());
  };

  const { isMobile } = useDeviceInfo();

  if (!vehicle) {
    return null;
  }

  const createInitialOption = (key: keyof Vehicle) => {
    const value = vehicle[key];
    if (!value) return null;
    if (vehicle.upload_type === 'manual') {
      return value;
    }
    return { value, label: value };
  };

  const initialValues = serializeUploadInitialValues(vehicle);

  const nextAction = () => {
    if (!isMobile) {
      close();
      history.push(`/vehicles/${vehicleId}/review`);
      return;
    }

    close();
    if (type !== 'mileage-update') {
      dispatch(openInitialPictureDialog({ vehicleId: vehicle.id, type: type! }));
    }
  };

  const handleSubmit = async (values: any, form: any) => {
    const { dirty } = form.getState();
    if (!dirty) {
      return nextAction();
    }

    const isManual = values.upload_type === 'manual';

    const parseValueField = (field: string) => {
      if (isManual) return values[field];
      return values[field]?.value;
    };

    await updateVehicle({
      id: vehicleId!,
      payload: {
        drive_train: parseValueField('drive_train'),
        engine: parseValueField('engine'),
        ext_color: parseValueField('ext_color'),
        make: values.make,
        model: values.model,
        mileage: values.mileage,
        options: values.options,
        transmission: parseValueField('transmission'),
        trim: parseValueField('trim'),
        year: values.year,
        reserve_price: values.reserve_price,
        starting_bid: values.starting_bid,
        bluebook_mileage: values.bluebook_mileage ? Number(values.bluebook_mileage) : undefined,
        bluebook_value: values.bluebook_value ? Number(values.bluebook_value) : undefined,
        bluebook_mileage_adjustment: values.bluebook_mileage_adjustment
          ? Number(values.bluebook_mileage_adjustment)
          : undefined
      }
    });
    if (type !== 'mileage-update') {
      refetch();
    }
    return nextAction();
  };

  return (
    <StyledDialog classes={{ paper: 'create-vehicle-info-paper' }} open={!!vehicleId} onBackdropClick={close}>
      <Form
        mutators={{
          ...arrayMutators
        }}
        keepDirtyOnReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {props => <RenderForm vehicleId={vehicleId} handleSubmit={props.handleSubmit} />}
      </Form>
    </StyledDialog>
  );
};

const BlueDriverInfoContainer = styled.div<{ isEqual: boolean }>`
  background: ${({ isEqual }) => (isEqual ? '#E9EDF7EE' : '#CA8D571A')};
  color: ${({ isEqual }) => (isEqual ? '#225A91' : '#CA8D57')};
  border-bottom: ${({ isEqual }) => (isEqual ? '1px solid #3994DE' : '1px solid #CA8D57')};
  font-size: 10px;
  font-weight: 500;
  line-height: 11.72px;
  padding-left: 8px;
  padding-top: 1px;
  margin-top: -7px;
`;

export const BlueDriverInfoComponent = ({ vehicleId, fieldName }: any) => {
  const { data: vehicle } = useGetVehicleByIdQuery(vehicleId!, { skip: !vehicleId });
  const { input, meta } = useField(fieldName);
  if (!vehicle || !vehicle.blueDriverInfo?.[fieldName] || !input.value) return null;

  const isEqual =
    String(vehicle.blueDriverInfo[fieldName]).toLocaleLowerCase() === String(input.value).toLocaleLowerCase();

  return (
    <BlueDriverInfoContainer isEqual={isEqual}>
      <span>{vehicle.blueDriverInfo[fieldName]}</span>
    </BlueDriverInfoContainer>
  );
};

const RenderForm = ({ handleSubmit, vehicleId }: any) => {
  const {
    input: { value: uploadType }
  } = useField('upload_type');

  const dispatch = useDispatch();

  const close = () => {
    dispatch(closeCarInfoDialog());
    dispatch(closeVinDialog());
  };

  const {
    input: { value: vin }
  } = useField('vin');

  const vinInfoState = useGetVinInfoQueryState(vin);

  const formState = useFormState();

  const {
    input: { value: bluebook_mileage_adjustment }
  } = useField('bluebook_mileage_adjustment');

  const hasMileage = uploadType === 'manual' || typeof bluebook_mileage_adjustment === 'number';

  const submitDisabled =
    vinInfoState.isFetching || formState.submitting || !hasMileage || Object.keys(formState.errors || {}).length > 0;

  const onMileageFetched = () => {
    const element: HTMLElement | null = document.querySelector('.create-vehicle-info-paper');
    if (element) element.scroll({ top: element.scrollHeight, behavior: 'smooth' });
  };

  return (
    <Wrapper onSubmit={handleSubmit}>
      <KbbIconWrapper>
        <KbbIcon />
      </KbbIconWrapper>
      <Header>
        <Title>Vehicle Information</Title>
        <CloseIcon onClick={close}>close</CloseIcon>
      </Header>
      <InputsWrapper>
        <ContainedTextField
          AdditionalComponent={() => <BlueDriverInfoComponent vehicleId={vehicleId} fieldName={'year'} />}
          validate={required}
          name="year"
          label="Year"
        />
        <ContainedTextField
          AdditionalComponent={() => <BlueDriverInfoComponent vehicleId={vehicleId} fieldName={'make'} />}
          validate={required}
          name="make"
          label="Make"
        />
        <ContainedTextField
          AdditionalComponent={() => <BlueDriverInfoComponent vehicleId={vehicleId} fieldName={'model'} />}
          validate={required}
          name="model"
          label="Model"
        />
        {uploadType === 'manual' ? <ManualFields /> : <AutomaticFields vehicleId={vehicleId} />}
        <ContainedNumberField
          validate={validateMinBid}
          name="starting_bid"
          label="Starting Bid"
          numberFormatProps={{
            allowNegative: false,
            thousandSeparator: true,
            decimalScale: 0,
            prefix: '$'
          }}
          parse={normalizeFormattedField}
        />
        <ContainedNumberField
          validate={validateReservePrice}
          name="reserve_price"
          parse={normalizeFormattedField}
          label="Reserve Price"
          numberFormatProps={{
            allowNegative: false,
            thousandSeparator: true,
            decimalScale: 0,
            prefix: '$'
          }}
        />
      </InputsWrapper>
      <OptionalEquipment defaultOpen />
      <MileageFields vehicleId={vehicleId} onMileageFetched={onMileageFetched} />
      <ActionsWrapper>
        <CancelAction fullWidth variant="contained" type="button" onClick={close}>
          Cancel
        </CancelAction>
        <SubmitAction disabled={submitDisabled} fullWidth variant="contained" type="submit">
          Save
        </SubmitAction>
      </ActionsWrapper>
    </Wrapper>
  );
};

const onMenuOpen = () => {
  document.body.style.touchAction = 'none';
  document.body.style.overflow = 'hidden';
};

const onMenuClose = () => {
  document.body.style.touchAction = '';
  document.body.style.overflow = '';
  (document.activeElement as HTMLElement | null)?.blur();
};

export const AutomaticFields = ({ vehicleId }: { vehicleId: number }) => {
  const {
    input: { value: trim }
  } = useField('trim');

  const {
    input: { value: uploadType }
  } = useField('upload_type');
  const { data: vinInfos = [] } = useGetVinInfoByVehicleIdQuery(vehicleId, {
    skip: !vehicleId || uploadType === 'manual'
  });

  const trimOptions = vinInfos?.map(info => ({ label: info.trim, value: info.trim })) || [];
  const vinInfo = vinInfos?.find(info => info.trim === trim.value);

  const createVinInfoOptions = (field: 'engines' | 'drive_trains' | 'transmissions' | 'colors') => {
    return (
      vinInfo?.[field]?.map(option => ({
        label: option.optionName,
        value: option.optionName
      })) || []
    );
  };

  const engineOptions = createVinInfoOptions('engines');
  const transmissionOptions = createVinInfoOptions('transmissions');
  const driveTrainOptions = createVinInfoOptions('drive_trains');
  const colorOptions = createVinInfoOptions('colors');

  const form = useForm();

  const resetTrimRelatedFields = () => {
    form.batch(() => {
      form.change('transmission', null);
      form.change('engine', null);
      form.change('drive_train', null);
      form.change('ext_color', null);
    });
  };

  const formState = useFormState();

  // useEffect(() => {
  //   if (!vinInfo || !formState.dirty) return;
  //   resetTrimRelatedFields();
  // }, [vinInfo]);

  const trimEmpty = !trim || (Array.isArray(trim) && trim.length === 0);

  const trimValue = trim?.value;

  // useEffect(() => {
  //   if (formState.dirty) resetTrimRelatedFields();
  // }, [trimValue]);

  return (
    <>
      <ContainedFormReactSelect
        onMenuClose={onMenuClose}
        onMenuOpen={onMenuOpen}
        menuPosition="absolute"
        thickArrow
        includeDelimiter={false}
        options={trimOptions}
        name="trim"
        label="Trim"
        validate={required}
        afterSelect={resetTrimRelatedFields}
      />
      <ContainedFormReactSelect
        AdditionalComponent={() => <BlueDriverInfoComponent vehicleId={vehicleId} fieldName={'engine'} />}
        thickArrow
        onMenuClose={onMenuClose}
        onMenuOpen={onMenuOpen}
        menuPosition="absolute"
        includeDelimiter={false}
        options={engineOptions}
        name="engine"
        label="Engine"
        validate={required}
        disabled={trimEmpty}
      />
      <ContainedFormReactSelect
        thickArrow
        onMenuClose={onMenuClose}
        onMenuOpen={onMenuOpen}
        menuPosition="absolute"
        includeDelimiter={false}
        options={transmissionOptions}
        name="transmission"
        label="Transmission"
        validate={required}
        disabled={trimEmpty}
      />
      <ContainedFormReactSelect
        thickArrow
        onMenuClose={onMenuClose}
        onMenuOpen={onMenuOpen}
        menuPosition="absolute"
        includeDelimiter={false}
        options={driveTrainOptions}
        name="drive_train"
        label="Drive Train"
        validate={required}
        disabled={trimEmpty}
      />
      <ContainedFormReactSelect
        thickArrow
        onMenuClose={onMenuClose}
        onMenuOpen={onMenuOpen}
        menuPosition="absolute"
        includeDelimiter={false}
        options={colorOptions}
        name="ext_color"
        validate={required}
        label="Ext. Color"
        disabled={trimEmpty}
      />
    </>
  );
};

export const ManualFields = () => {
  return (
    <>
      <ContainedTextField name="trim" label="Trim" validate={required} />
      <ContainedTextField validate={required} name="engine" label="Engine" />
      <ContainedTextField validate={required} name="transmission" label="Transmission" />
      <ContainedTextField validate={required} name="drive_train" label="Drive Train" />
      <ContainedTextField validate={required} name="ext_color" label="Ext. Color" />
    </>
  );
};
